import { Box, Stack, Tab, TabGroup } from '@chronosphereio/chrono-ui';
import { useFeatureFlags } from '@chronosphereio/core';
import { truncate } from '@chronosphereio/shared';
import { useState } from 'react';
import { useReplaceVariablesInString } from '@/plugin-system';
import { ErrorAlert, ErrorBoundary } from '../../../components';
import {
  PanelGroupProvider,
  useDashboardMetadata,
  useEditMode,
  useListPanelGroups,
  usePanelGroupIds,
} from '../../context';
import { EmptyDashboard, EmptyDashboardProps } from '../EmptyDashboard';
import { SplitGridLayout } from '../GridLayout/SplitGridLayout';
import { PanelOptions } from '../Panel';
export type DashboardProps = {
  /**
   * Props for `EmptyDashboard` component that will be rendered when the dashboard
   * is empty (i.e. has no panel groups). If not specified, the defaults will
   * be used.
   */
  emptyDashboardProps?: EmptyDashboardProps;
  panelOptions?: PanelOptions;
};

/**
 * Renders a Dashboard for the provided Dashboard spec.
 */
export function Dashboard({ emptyDashboardProps, panelOptions }: DashboardProps) {
  const { devErgoTabbedLayout } = useFeatureFlags();
  const { metadata } = useDashboardMetadata();
  const { isEditMode } = useEditMode();
  const isTabbedLayout = (devErgoTabbedLayout && metadata.tags?.includes('tabbed-layout') && !isEditMode) ?? false;

  return isTabbedLayout ? (
    <TabbedDashboard emptyDashboardProps={emptyDashboardProps} panelOptions={panelOptions} />
  ) : (
    <StackedDashboard emptyDashboardProps={emptyDashboardProps} panelOptions={panelOptions} />
  );
}

interface TabbedDashboardTabTitleProps {
  title?: string;
  index: number;
}

const TabbedDashboardTabTitle = ({ title, index }: TabbedDashboardTabTitleProps) => {
  const rawTitle = title || `Group ${index + 1}`;
  const parsedTitle = useReplaceVariablesInString(rawTitle);

  return <>{truncate(parsedTitle ?? '', 30)}</>;
};

function TabbedDashboard({ emptyDashboardProps, panelOptions }: DashboardProps) {
  const panelGroupIds = usePanelGroupIds();
  const panelGroups = useListPanelGroups();
  const isEmpty = !panelGroupIds.length;
  const [activeTab, setActiveTab] = useState(0);
  const showTabs = !isEmpty && panelGroups.length > 1;

  return (
    <ErrorBoundary FallbackComponent={ErrorAlert}>
      {isEmpty && (
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
          <EmptyDashboard {...emptyDashboardProps} />
        </Box>
      )}
      <Stack spacing={2}>
        {showTabs && (
          <TabGroup value={activeTab} onChange={(_event, newValue) => setActiveTab(newValue)} aria-label="Panel groups">
            {panelGroups.map((group, index) => (
              <Tab key={group.id} value={index}>
                <TabbedDashboardTabTitle title={group.title} index={index} />
              </Tab>
            ))}
          </TabGroup>
        )}
        {!isEmpty &&
          panelGroupIds.map((panelGroupId, index) => {
            // When tabs are shown, only render the active tab's group
            if (showTabs && index !== activeTab) {
              return null;
            }

            return (
              <PanelGroupProvider key={`panel-group-${panelGroupId}`} panelGroupId={panelGroupId}>
                <SplitGridLayout
                  key={`grid-${panelGroupId}`}
                  panelGroupId={panelGroupId}
                  panelGroupIndex={index}
                  panelOptions={panelOptions}
                  omitTitle={showTabs}
                />
              </PanelGroupProvider>
            );
          })}
      </Stack>
    </ErrorBoundary>
  );
}

function StackedDashboard({ emptyDashboardProps, panelOptions }: DashboardProps) {
  const panelGroupIds = usePanelGroupIds();
  const isEmpty = !panelGroupIds.length;

  return (
    <ErrorBoundary FallbackComponent={ErrorAlert}>
      {isEmpty && (
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
          <EmptyDashboard {...emptyDashboardProps} />
        </Box>
      )}
      <Stack spacing={1}>
        {!isEmpty &&
          panelGroupIds.map((panelGroupId, index) => (
            <PanelGroupProvider key={`panel-group-${panelGroupId}`} panelGroupId={panelGroupId}>
              <SplitGridLayout
                key={`grid-${panelGroupId}`}
                panelGroupId={panelGroupId}
                panelGroupIndex={index}
                panelOptions={panelOptions}
              />
            </PanelGroupProvider>
          ))}
      </Stack>
    </ErrorBoundary>
  );
}
